import { useEffect, useState } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { GetMirFiltersProps } from '@organisms/manageInventory/MIR/mirUtils';
import { GetMaterialsDTO } from 'APIServices/materialsInReview/MaterialsInReview.types';
import { GetOrdersDTO } from 'APIServices/trackOrders/TrackOrdersApi.types';
import { GetOrderFiltersProps } from 'components/UI/organisms/trackOrders/orderList/ordersListUrlUtils';
import { getBasicSort } from 'utils/helpers/getBasePaginationBody';
import {
    FullStatusList,
    DefaultActiveStatuses
} from 'utils/helpers/orderStatus';
import { BASIC_SEARCH_PARAMS } from 'utils/helpers/paramsHandler';
import { Sorting } from 'utils/types/general/general.types';
import {
    OrdersListSearchParams,
    MaterialsListSearchParams
} from 'utils/types/mirAndTracking/MirAndTracking.types';

type CommonProps = {
    initialValue?: string;
    removeParam: <T>(params: string[], paramsToSave?: T) => void;
    sorting: Sorting;
    withReplace?: boolean;
    isMobile?: boolean;
};

type OrderSearchProps = {
    setParams: (newParams?: OrdersListSearchParams, replace?: boolean) => void;
    config: GetOrdersDTO;
    getData: (config: GetOrdersDTO) => void;
    getFilters: (
        data?: GetOrderFiltersProps,
        ignoreFilterDates?: boolean,
        defaultDateRangeInDays?: number
    ) => string;
    defaultDateRangeInDays: number;
    status?: string;
};

type MirSearchProps = {
    setParams: (
        newParams?: MaterialsListSearchParams,
        replace?: boolean
    ) => void;
    config: GetMaterialsDTO;
    getData: (config: GetMaterialsDTO) => void;
    getFilters: (
        data?: GetMirFiltersProps,
        ignoreFilterDates?: boolean,
        defaultDateRangeInDays?: number
    ) => string;
    defaultDateRangeInDays?: number;
    status?: string;
};

type SearchFilterProps = CommonProps & (OrderSearchProps | MirSearchProps);

export const useSearchFilter = ({
    initialValue = '',
    setParams,
    removeParam,
    getData,
    config,
    sorting,
    status,
    getFilters,
    defaultDateRangeInDays,
    withReplace = true,
    isMobile = false
}: SearchFilterProps) => {
    const [searchValue, setSearchValue] = useState('');

    useEffect(() => {
        setSearchValue(initialValue);
    }, [initialValue]);

    const onChangeSearch = (value: string) => {
        setSearchValue(value);
    };

    const handleSearchAction = (value: string) => {
        setParams(
            {
                [BASIC_SEARCH_PARAMS.SORTS]: getBasicSort(sorting),
                [BASIC_SEARCH_PARAMS.SEARCH]: value,
                [BASIC_SEARCH_PARAMS.STATUS]: isMobile
                    ? FullStatusList.join('|')
                    : status,
                [BASIC_SEARCH_PARAMS.PAGE]: 1,
                [BASIC_SEARCH_PARAMS.PAGE_SIZE]: config.pageSize
            },
            withReplace
        );

        const baseSort = getBasicSort(sorting);

        getData({
            ...config,
            sorts: baseSort,
            page: 1,
            filters: getFilters(
                {
                    search: value,
                    status
                },
                true
            )
        });
    };

    const getDebouncedSearch = useDebouncedCallback((value: string) => {
        handleSearchAction(value);
    }, 1000);

    const onSearchInputClean = () => {
        setSearchValue('');
        removeParam([BASIC_SEARCH_PARAMS.SEARCH, BASIC_SEARCH_PARAMS.PAGE], {
            [BASIC_SEARCH_PARAMS.STATUS]: isMobile
                ? DefaultActiveStatuses.join('|')
                : status
        });

        getData({
            ...config,
            filters: getFilters({ status }, false, defaultDateRangeInDays),
            page: 1
        });
    };

    const onChangeSearchDynamically = (value: string) => {
        setSearchValue(value);
        getDebouncedSearch.cancel();

        if (!value) {
            onSearchInputClean();
            return;
        }

        const trimmedValue = value.trim();

        if (trimmedValue === searchValue.trim()) {
            return;
        }

        if (trimmedValue.length) {
            getDebouncedSearch(trimmedValue);
            return;
        }
    };

    const onSubmitSearch = (value: string) => {
        if (!value) {
            onSearchInputClean();
        } else {
            handleSearchAction(value.trim());
        }
    };

    return {
        searchValue,
        onChangeSearch,
        onChangeSearchDynamically,
        onSearchInputClean,
        onSubmitSearch
    };
};
